import { Button, Typography } from '@mui/material'
import { forwardRef } from 'react'
import {
  MaskedInputField,
  MaskedInputFieldProps,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type PaymentCreateFormProps = {
  onSubmitCreatePayment: (values: PaymentFormValues) => Promise<void>
}

export type PaymentFormValues = {
  cardNumber: string
  cardHolder: string
  securityCode: string
  expirationDate: string
}

const schema: UseFormProps['schema'] = o => ({
  cardNumber: o.string().label(tLabel('paymentCreate.cardNumber')),
  cardHolder: o.string().label(tLabel('paymentCreate.cardHolder')),
  securityCode: o.string().label(tLabel('paymentCreate.securityCode')),
  expirationDate: o
    .string()
    .pattern(new RegExp('^(0[1-9]|1[0-2])/?([0-9]{2})$'))
    .label(tLabel('paymentCreate.expirationDate')),
})

const defaultValues = {
  cardNumber: '',
  cardHolder: '',
  securityCode: '',
  expirationDate: '',
}

export const PaymentCreateForm: Organism<PaymentCreateFormProps> = ({
  onSubmitCreatePayment,
}) => {
  const { t } = useTranslation('paymentCreate')
  const { showMessage, showError } = useMessage()
  const {
    handleSubmit,
    register,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PaymentFormValues>({
    schema,
    defaultValues,
  })

  const onSubmit = handleSubmit(values =>
    onSubmitCreatePayment(values)
      .then(() => {
        showMessage(t('success'))
        reset(defaultValues)
      })
      .catch(e => handleError<PaymentFormValues>(e, setError, showError))
  )

  return (
    <Form onSubmit={onSubmit}>
      <Typography variant="h5" sx={{ mt: 2, mb: 4 }}>
        {t('register')}
      </Typography>
      <MaskedInputCell
        type="text"
        inputProps={{
          pattern: '[0-9\\s]*',
          inputMode: 'numeric',
        }}
        mask="0000 0000 0000 0000"
        error={errors?.cardNumber}
        {...register('cardNumber')}
      />
      <TextInputCell
        type="text"
        error={errors?.cardHolder}
        {...register('cardHolder')}
      />
      <MaskedInputCell
        type="text"
        inputProps={{
          pattern: '[0-9\\s]*',
          inputMode: 'numeric',
        }}
        mask="0000"
        placeholder={t('')}
        error={errors?.securityCode}
        {...register('securityCode')}
      />
      <MaskedInputCell
        type="text"
        inputProps={{
          pattern: '[0-9/]*',
          inputMode: 'numeric',
        }}
        mask="00/00"
        error={errors?.expirationDate}
        {...register('expirationDate')}
      />
      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('paymentCreate')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const MaskedInputCell = forwardRef(
  (props: Omit<MaskedInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('paymentCreate')
    return (
      <CellFrame>
        <MaskedInputField
          {...props}
          ref={ref}
          t={t}
          placeholder={t(`${props.name}Placeholder`)}
        />
      </CellFrame>
    )
  }
)

const ButtonCell = ({ loading }: { loading: boolean }) => {
  const { t } = useTranslation('paymentCreate')
  return (
    <CellFrame sx={{ px: 4, py: 3, textAlign: 'center' }}>
      <Button
        variant="contained"
        type="submit"
        size="large"
        sx={{ width: 244 }}
        loading={loading}
      >
        {t('submit')}
      </Button>
    </CellFrame>
  )
}
